import React, { useState } from "react";
import { ArrowUpRight } from "lucide-react";

const ServicesSection: React.FC = () => {
  const [hoveredService, setHoveredService] = useState<number>(0);

  const services = [
    {
      number: "01",
      title: "Discover",
      description:
        "We work alongside your strategy team to identify high-impact ideas worth testing, mapping out what needs to be built to validate them quickly.",
      gradient: "from-orange-400 via-pink-500 to-purple-500",
    },
    {
      number: "02",
      title: "Build",
      description:
        "We're boots on the ground—we build the systems, tools, and prototypes your team needs to test ideas in days, not months.",
      gradient: "from-blue-400 via-indigo-500 to-purple-500",
    },
    {
      number: "03",
      title: "Deploy",
      description:
        "We launch and integrate solutions into your workflow immediately, so your team can start testing and learning right away.",
      gradient: "from-green-400 via-teal-500 to-blue-500",
    },
    {
      number: "04",
      title: "Optimize",
      description:
        "We iterate based on what you learn, scaling what works and pivoting fast when ideas don't pan out—no wasted time or resources.",
      gradient: "from-pink-400 via-rose-500 to-red-500",
    },
  ];

  return (
    <>
    <section className="py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-block bg-gray-100 rounded-full px-4 py-2 mb-6">
            <span className="text-sm font-medium text-gray-600 tracking-wider">
              Services
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light leading-tight mb-6">
            We build <span className="text-gray-500">fast</span> so you can test
            faster.
          </h2>
          <p className="text-lg text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Strategy teams need to validate ideas quickly. We're your execution
            partner we build the systems and tools you need to test, learn, and
            iterate without the overhead of internal development cycles.
          </p>
        </div>

        {/* Services Grid - Centered and Very Close Together */}
        <div className="flex justify-center">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-2 items-start max-w-4xl w-full">
            {/* Left Side - Service Steps */}
            <div className="space-y-4 sm:space-y-6 lg:space-y-8 lg:pr-2 text-center lg:text-left">
              {services.map((service, index) => (
                <div
                  key={index}
                  className="cursor-pointer transition-all duration-300 py-3"
                  onMouseEnter={() => setHoveredService(index)}
                >
                  <div className="flex items-baseline gap-3 justify-center lg:justify-start">
                    <h3
                      className={`text-4xl sm:text-5xl lg:text-6xl font-light transition-colors duration-300 ${
                        hoveredService === index
                          ? "text-gray-900"
                          : "text-gray-300"
                      }`}
                    >
                      {service.title}
                    </h3>
                    <span className="text-xs text-gray-400 font-mono">
                      {service.number}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Right Side - Dynamic Card */}
            <div className="lg:sticky lg:top-8 lg:pl-2">
              <div
                className={`rounded-3xl h-[320px] sm:h-[400px] lg:h-[520px] w-full relative overflow-hidden transition-all duration-500 shadow-[0_20px_60px_rgba(0,0,0,0.08)] bg-gradient-to-br ${services[hoveredService].gradient}`}
              >
                <div className="absolute inset-0 bg-gradient-to-tr from-white/10 via-transparent to-white/10 mix-blend-screen pointer-events-none" />
                {/* Content Overlay */}
                <div className="absolute inset-0 flex flex-col justify-between p-6 sm:p-8 lg:p-12 bg-black/15 backdrop-blur-[1px]">
                  {/* Service Title */}
                  <div>
                    <h4 className="text-2xl sm:text-3xl lg:text-4xl font-light text-white mb-4 sm:mb-6">
                      {services[hoveredService].title}
                    </h4>
                    <p className="text-white text-base sm:text-lg leading-relaxed max-w-md">
                      {services[hoveredService].description}
                    </p>
                  </div>

                  {/* CTA Button */}
                  <a
                    href="https://calendly.com/rohansontakke/30min"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-white hover:gap-3 transition-all duration-300 group self-start"
                  >
                    <ArrowUpRight className="w-5 h-5" />
                    <span className="text-lg font-light">Book a free call</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* How we work - beige section */}
    <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#f7f1e8]">
      <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-start">
        <div className="space-y-6">
          <span className="inline-block bg-[#efe5d8] text-[#5a4333] text-sm font-medium px-4 py-2 rounded-full shadow-sm">
            How we work
          </span>
          <h3 className="text-3xl sm:text-4xl md:text-5xl font-light leading-tight text-[#2f2117]">
            Rapid testing <br />
            <span className="text-[#9b8778]">without</span> the overhead.
          </h3>
          <p className="text-base text-[#6f5c4f] leading-relaxed max-w-xl">
            We work directly with your strategy team to turn ideas into testable
            systems fast—no lengthy RFP processes, no vendor handoffs. Just rapid
            execution so you can learn what works.
          </p>
          <div className="flex flex-wrap gap-4">
            <a
              href="https://calendly.com/rohansontakke/30min"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#2e1c14] text-white px-6 py-3 rounded-xl font-semibold hover:bg-[#25150f] transition-colors"
            >
              Book a free call
            </a>
          </div>
        </div>

        <div className="relative">
          <div className="absolute left-6 top-8 bottom-8 w-px bg-gradient-to-b from-transparent via-[#e4d6c7] to-transparent" />
          <div className="space-y-8 relative">
            {[
              {
                title: "Ideate & Scope",
                subtitle:
                  "We work with your strategy team to identify testable ideas and map out what needs to be built to validate them quickly.",
                chip: "Days 1-3",
              },
              {
                title: "Build & Launch",
                subtitle:
                  "We're boots on the ground—we build the systems and tools you need, then deploy them so your team can start testing immediately.",
                chip: "Days 4-14",
              },
              {
                title: "Iterate & Scale",
                subtitle:
                  "We iterate based on what you learn, scaling what works and pivoting fast when ideas don't pan out—continuous rapid testing.",
                chip: "Ongoing",
              },
            ].map((step, idx) => (
              <div key={idx} className="flex gap-4 items-start">
                <div className="w-12 h-12 rounded-full bg-white shadow-sm flex items-center justify-center text-[#2e1c14] font-semibold">
                  0{idx + 1}
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <h4 className="text-lg font-semibold text-[#2e1c14]">
                      {step.title}
                    </h4>
                    <span className="inline-block bg-[#efe5d8] text-[#4c3a2e] text-xs font-semibold px-3 py-1 rounded-full shadow-sm">
                      {step.chip}
                    </span>
                  </div>
                  <p className="text-[#6f5c4f] leading-relaxed text-sm">
                    {step.subtitle}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
    </>
  );
};

export default ServicesSection;
